import { Lock } from '@phosphor-icons/react';
import { useDemo } from '../../../../../../utils/DemoContext';

/**
 * DemoDataAlert
 *
 * Alert banner that informs users they're viewing preview/demo data.
 * Displayed prominently at the top of the dashboard for pre-active customers.
 * Persists until customer status changes to 'active'.
 *
 * Design: Eye-catching gradient, non-dismissible, clear messaging
 */

interface DemoDataAlertProps {
  /** Custom message (optional) */
  message?: string;
}

const DEFAULT_MESSAGE =
  "You're viewing preview data to help you explore TACH. Complete your onboarding steps and receive bank approval to unlock your real account data and features.";

export function DemoDataAlert({
  message = DEFAULT_MESSAGE,
}: DemoDataAlertProps) {
  const { isDemo } = useDemo();
  if (isDemo) return null;

  return (
    <div className="animate-in fade-in w-full duration-500">
      {/* Alert Banner */}
      <div className="relative overflow-hidden rounded-lg border border-blue-200 bg-gradient-to-r from-blue-50 via-teal-50 to-green-50 shadow-sm">
        {/* Subtle animated background pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute left-0 top-0 h-full w-1/3 bg-gradient-to-r from-blue-200/20 to-transparent" />
          <div className="absolute right-0 top-0 h-full w-1/3 bg-gradient-to-l from-green-200/20 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative flex items-start gap-4 p-4 md:items-center md:p-5">
          {/* Icon */}
          <div className="flex-shrink-0">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-secondary">
              <Lock size={20} weight="fill" className="text-white" />
            </div>
          </div>

          {/* Message */}
          <div className="flex-1">
            <div className="mb-1 flex items-center gap-2">
              <h3 className="text-base font-bold text-gray-900 md:text-lg">
                Preview Mode Active
              </h3>
              <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-700">
                Demo Data
              </span>
            </div>
            <p className="text-sm leading-relaxed text-gray-700 md:text-base">
              {message}
            </p>
          </div>
        </div>

        {/* Bottom accent line */}
        <div className="h-1 w-full bg-gradient-to-r from-blue-600 via-teal-500 to-secondary" />
      </div>
    </div>
  );
}

/**
 * Compact version for mobile or tight spaces
 */
export function DemoDataAlertCompact() {
  const { isDemo } = useDemo();
  if (isDemo) return null;

  return (
    <div className="animate-in fade-in w-full duration-500">
      <div className="relative overflow-hidden rounded-lg border border-blue-200 bg-gradient-to-r from-blue-50 to-green-50">
        <div className="flex items-center gap-3 p-3">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-secondary">
            <Lock size={16} weight="fill" className="text-white" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-900">
              Viewing preview data
            </p>
            <p className="text-xs text-gray-600">
              Complete onboarding to unlock real features
            </p>
          </div>
        </div>
        <div className="h-0.5 w-full bg-gradient-to-r from-blue-600 via-teal-500 to-secondary" />
      </div>
    </div>
  );
}
